import React, { useState } from 'react';
import { Send, Sparkles, MessageSquare, Copy, Check, Info, Zap, RefreshCw } from 'lucide-react';
import { config } from '../../config';

const ScriptGenerator = ({ role }) => {
    const [prompt, setPrompt] = useState('');
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [scanLoading, setScanLoading] = useState(false);
    const [copied, setCopied] = useState(false);
    const [scanResult, setScanResult] = useState(null);

    const isAdmin = role === 'ADMIN';

    const forceScan = async () => {
        setScanLoading(true);
        setScanResult(null);
        try {
            const response = await fetch(`${config.API_BASE_URL}/webhook/scripts/monitor/trigger`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'force-scan-all' })
            });
            if (response.ok) {
                setScanResult("Scan Completed Successfully!");
                setTimeout(() => setScanResult(null), 3000);
            } else {
                throw new Error("Scan Failed");
            }
        } catch (err) {
            console.error("Manual scan failed:", err);
            setScanResult("Scan Failed. Check Connection.");
        } finally {
            setScanLoading(false);
        }
    };

    const generateScript = async () => {
        if (!prompt.trim()) {return;}
        setLoading(true);
        setResult(null);

        try {
            const response = await fetch(`${config.API_BASE_URL}/webhook/chat/webhook`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'send_message',
                    recipient: 'AI_AGENT',
                    content: `[SWAP_REQUEST] Analyze this and provide a BizBuilder Swap: "${prompt}"`,
                    threadId: `swap-gen-${config.USER_EMAIL}`,
                    sender: config.USER_EMAIL,
                    context: { label: 'Script Generator', type: 'SWAP' }
                })
            });

            const data = await response.json();
            setResult(data.Content || "No suggestion found. Try rephrasing.");
        } catch (err) {
            console.error("AI Generation failed:", err);
            setResult("Failed to generate script. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = () => {
        if (!result) {return;}
        navigator.clipboard.writeText(result);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="relative p-[1px] bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-600 rounded-[2.5rem] shadow-2xl shadow-blue-900/10 group overflow-hidden animate-in fade-in zoom-in duration-500">
            <div className="absolute inset-0 bg-white/40 backdrop-blur-3xl rounded-[2.5rem]" />

            <div className="relative p-8 bg-white/80 backdrop-blur-xl rounded-[2.5rem] border border-white/40">
                <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-4">
                        <div className="bg-gradient-to-br from-blue-600 to-indigo-700 p-3.5 rounded-2xl text-white shadow-xl shadow-blue-500/20 ring-4 ring-blue-50">
                            <Sparkles size={24} className="animate-pulse" />
                        </div>
                        <div>
                            <h3 className="text-lg font-black text-slate-900 uppercase tracking-widest leading-none mb-1.5">Script Generator</h3>
                            <div className="flex items-center gap-2">
                                <span className="h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse" />
                                <p className="text-[10px] text-slate-500 font-black uppercase tracking-[0.2em]">Hybrid AI Active</p>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        {isAdmin && (
                            <button
                                onClick={forceScan}
                                disabled={scanLoading}
                                className={`flex items-center gap-2 px-4 py-2 rounded-full border transition-all duration-300 ${scanResult ? 'bg-green-50 border-green-200 text-green-600' : 'bg-slate-900/5 border-slate-900/5 text-slate-500 hover:bg-slate-900/10'
                                    }`}
                            >
                                <RefreshCw size={12} className={scanLoading ? 'animate-spin' : ''} />
                                <span className="text-[9px] font-black uppercase tracking-widest">
                                    {scanResult || (scanLoading ? 'Scanning...' : 'Force Scan')}
                                </span>
                            </button>
                        )}
                        <div className="hidden sm:flex bg-slate-900/5 px-4 py-2 rounded-full border border-slate-900/5 items-center gap-2">
                            <MessageSquare size={12} className="text-slate-400" />
                            <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Goal-to-10 Logic</span>
                        </div>
                    </div>
                </div>

                <div className="space-y-6">
                    <div className="relative group/input">
                        <textarea
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            placeholder="Paste a client message here (e.g. 'How much does this cost?')..."
                            className="w-full h-40 p-6 bg-slate-50/50 backdrop-blur-sm border-2 border-slate-100 rounded-3xl text-sm font-bold text-slate-700 placeholder:text-slate-300 focus:border-blue-500 focus:bg-white transition-all resize-none outline-none shadow-inner"
                        />
                        <button
                            onClick={generateScript}
                            disabled={loading || !prompt.trim()}
                            className="absolute bottom-4 right-4 px-6 py-3 bg-slate-900 text-white rounded-2xl hover:bg-blue-600 disabled:bg-slate-200 disabled:cursor-not-allowed transition-all duration-300 flex items-center gap-2 shadow-xl hover:shadow-blue-500/25 active:scale-95 font-black text-[10px] uppercase tracking-widest"
                        >
                            {loading ? (
                                <div className="flex items-center gap-2">
                                    <div className="animate-spin rounded-full h-3 w-3 border-2 border-white/20 border-t-white" />
                                    <span>Generating...</span>
                                </div>
                            ) : (
                                <>
                                    <span>Generate Swap</span>
                                    <Send size={14} />
                                </>
                            )}
                        </button>
                    </div>

                    {result && (
                        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                            <div className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50/50 border border-blue-100 rounded-[2rem] relative shadow-sm overflow-hidden">
                                <div className="absolute top-0 right-0 p-8 opacity-5">
                                    <Check size={120} />
                                </div>
                                <div className="flex items-center gap-2 mb-4">
                                    <div className="h-1 w-8 bg-blue-600 rounded-full" />
                                    <p className="text-[10px] font-black text-blue-600 uppercase tracking-widest">Suggested Strategic Response</p>
                                </div>
                                <p className="text-sm font-bold text-slate-800 leading-relaxed pr-10 mb-4 selection:bg-blue-100 italic">
                                    "{result}"
                                </p>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <Zap size={12} className="text-amber-400 fill-amber-400" />
                                        <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Curriculum Compliant</span>
                                    </div>
                                    <button
                                        onClick={copyToClipboard}
                                        className="flex items-center gap-2 px-4 py-2 bg-white border border-blue-100 rounded-xl text-blue-600 hover:bg-blue-600 hover:text-white transition-all duration-300 shadow-sm active:scale-95"
                                    >
                                        {copied ? (
                                            <>
                                                <Check size={14} className="text-green-500" />
                                                <span className="text-[9px] font-black uppercase">Copied!</span>
                                            </>
                                        ) : (
                                            <>
                                                <Copy size={14} />
                                                <span className="text-[9px] font-black uppercase">Copy to WhatsApp</span>
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="flex items-start gap-3 p-4 bg-slate-900/[0.02] rounded-2xl border border-slate-900/[0.03]">
                        <Info size={16} className="text-slate-400 mt-0.5" />
                        <div>
                            <p className="text-[9px] font-black text-slate-400 uppercase leading-relaxed tracking-widest">
                                Privacy Protocol Active
                            </p>
                            <p className="text-[8px] font-bold text-slate-300 uppercase leading-none mt-1">
                                PII is automatically masked for all coached sessions.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ScriptGenerator;
